/** PATCH helper for the pre-trip item editor. Items have no `updated_ts` —
 *  writes are last-writer-wins, so there is no optimistic-concurrency token and
 *  no 409 flow (like regions). Sends multipart when a File is staged (to swap
 *  the attachment), else plain JSON. */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import * as itemsApi from '@/api/preTripItems';
import type { PreTripItemDetail, PreTripItemWriteJson } from '@/api/preTripItems';
import { useSnackbar } from '@/components/Snackbar';

export function usePreTripItemPatch(item: PreTripItemDetail, onSaved: () => void) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ body, attachment }: { body: PreTripItemWriteJson; attachment?: File | null }) =>
      itemsApi.update(item.id, body, attachment),
    onSuccess: () => {
      snackbar.show('Saved.', 'success');
      qc.invalidateQueries({ queryKey: ['pre_trip_items'] });
      onSaved();
    },
    onError: (e) => {
      const msg = e instanceof ConsoleApiError ? e.message : 'Save failed.';
      snackbar.show(msg, 'error');
    },
  });

  return {
    save: (body: PreTripItemWriteJson, attachment?: File | null) => mutation.mutate({ body, attachment }),
    saving: mutation.isPending,
    /** Field-keyed server errors from a 400, if any. */
    fieldErrors:
      mutation.error instanceof ConsoleApiError && mutation.error.status === 400
        ? (mutation.error.body as Record<string, unknown>)
        : null,
  };
}
